import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import useStyles from "./TextAnswerRenderer.styles";
import { RendererProps } from "../BaseRenderer";
import { QuestionType } from "../../../models/enums/QuestionType";
import { removeHtmlTags } from "../../../utils/index";
import { QuestionModel } from "../../../models/QuestionModel";
import InputBox from "../../shared/InputBox";
import useQuestion from "../../../hooks/useQuestion";
import useTranslatable from "../../../hooks/useTranslatable";
import { getSettingString } from "../../../models/utilities/settingUtilities";
import { size } from "../../../models/utilities/questionUtilities";
import {
    getAriaLabelledByFromId,
    useAriaLabelByForGridRenderedInputFieldQuestion,
    useAriaLabelledByForInputFieldQuestion,
} from "../../../utils/WCAGHelper";
import { useKeyboardNavigation } from "../../../hooks/useKeyboardNavigation";
import { TextAnswerFieldNavigation } from "../../controls/KeyboardNavigation/TextAnswerFieldNavigation";

interface Props extends RendererProps {
    multiline?: boolean;
    inputType?: string;
    placeholder?: string;
    inputDivRef?: React.LegacyRef<HTMLDivElement>;
    inputRef?: React.LegacyRef<HTMLInputElement & HTMLTextAreaElement>;
    min?: number;
    max?: number;
    maxLength?: number;
    onBlur?: React.FocusEventHandler;
    onFocus?: React.FocusEventHandler;
    onKeyDown?: React.KeyboardEventHandler;
    onKeyPress?: React.KeyboardEventHandler;
    onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    openDatePicker?(): void;
    value?: string;
    disabled?: boolean;
    lines?: number;
    inputMode?: "search" | "text" | "tel" | "none" | "url" | "email" | "numeric" | "decimal";
    isProbQuestion?: boolean;
}

function hasDatePicker(question: QuestionModel) {
    return ["Date", "DateTime", "Time"].includes(getSettingString(question, "Type"));
}

export default function TextAnswerRenderer(props: Props) {
    const [focused, setFocused] = React.useState(false);

    const {
        gridRendering,
        multiline,
        inputType,
        placeholder,
        min,
        max,
        maxLength,
        onBlur,
        onFocus,
        onKeyDown: propsOnKeyDown,
        onKeyPress,
        onChange,
        value,
        disabled,
        lines,
        inputMode,
        gridRenderedColumnIndex,
        gridRenderedRowIndex,
        isProbQuestion
    } = props;

    const theme = useTheme();
    const classes = useStyles({ theme, gridRendering });
    const question = useQuestion();
    const questionText = useTranslatable(question, "TEXT");
    const leftLabel = useTranslatable(question, "LeftLabel");
    const rightLabel = useTranslatable(question, "RightLabel");
    const questionId = question?.parent?.elementId ?? question?.elementId;
    const questionType = question?.parent?.type ?? question?.type;

    const [variable] = question.variables;

    const useAriaLabelledBy = useAriaLabelledByForInputFieldQuestion(question);
    const isGridRenderedLabellingInputBox = useAriaLabelByForGridRenderedInputFieldQuestion(
        question,
    );

    const isHybridGridRenderedTextFieldTypeQuestion =
        gridRendering &&
        questionType === QuestionType.HybridGrid &&
        (question.type === QuestionType.NumericAnswer || question.type === QuestionType.TextAnswer);

    // Default should be aria-label if not grid rendered and aria-labelled by for variable type in grid
    const useAriaLabel = !useAriaLabelledBy && !gridRendering && isGridRenderedLabellingInputBox;

    const classNames = classnames(
        classes.textAnswer,
        multiline && classes.textAnswerMultiLine,
        question.type === QuestionType.NumericAnswer && classes.textAnswerNumeric,
        question.type === QuestionType.DateTimeAnswer && classes.textAnswerDateTime,
    );

    const inputOnBlur = (e) => {
        setFocused(false);
        if (onBlur) {
            onBlur(e);
        }
    };

    const inputOnFocus = (e) => {
        setFocused(true);
        if (onFocus) {
            onFocus(e);
        }
    };

    const accessory: IconProp = hasDatePicker(question) ? ["far", "calendar-alt"] : null;

    const ariaAttributes = {};
    if (useAriaLabelledBy && !isHybridGridRenderedTextFieldTypeQuestion) {
        ariaAttributes["aria-labelledby"] = getAriaLabelledByFromId(question, questionId);
    } else if (useAriaLabel) {
        const questionAriaText = removeHtmlTags(questionText);
        if (questionAriaText) {
            ariaAttributes["aria-label"] = questionAriaText;
        }
    } else if (!isGridRenderedLabellingInputBox || isHybridGridRenderedTextFieldTypeQuestion) {
        ariaAttributes["aria-labelledby"] = getAriaLabelledByFromId(
            question,
            questionId,
            `${questionType}_INPUTBOX`,
            gridRenderedColumnIndex + 1,
            gridRenderedRowIndex + 1,
            gridRenderedColumnIndex + 1, // In hybrid question we have sub-question type so required column level index
        );
    }

    const aiProbeVariable: { elementId?: string, id?: string } = {};
    if (isProbQuestion) {
        aiProbeVariable['elementId'] = `${variable.elementId}_AI_PROBE`;
        aiProbeVariable['id'] = `${variable.id}_AI_PROBE`;
    }

    const elementId = isProbQuestion ? aiProbeVariable.elementId : variable.elementId;
    const { elementReference, onKeyDown: keyboardNavOnKeyDown } = useKeyboardNavigation(
        `${elementId}_INPUT`,
        isProbQuestion ? aiProbeVariable : variable,
        question,
        TextAnswerFieldNavigation,
        null,
        isProbQuestion
    );

    // Combine keyboard navigation handler with custom onKeyDown prop
    const combinedOnKeyDown = (event: React.KeyboardEvent) => {
        // First call the custom onKeyDown handler if provided
        if (propsOnKeyDown) {
            propsOnKeyDown(event);
        }
        // Then call keyboard navigation handler if event wasn't prevented
        if (!event.defaultPrevented && keyboardNavOnKeyDown) {
            keyboardNavOnKeyDown(event);
        }
    };

    return (
        <InputBox
            focused={focused}
            freeform={multiline}
            size={size(question)}
            leftLabel={leftLabel}
            rightLabel={rightLabel}
            className={classNames}
            accessoryRight={accessory}
            disabled={disabled}
        >
            {multiline && (
                <textarea
                    id={`${elementId}_INPUT`}
                    {...ariaAttributes}
                    name={variable.elementId}
                    rows={lines || 2}
                    placeholder={placeholder}
                    disabled={disabled}
                    ref={elementReference}
                    maxLength={maxLength}
                    onBlur={inputOnBlur}
                    onFocus={inputOnFocus}
                    onKeyDown={combinedOnKeyDown}
                    onKeyPress={onKeyPress}
                    onChange={onChange}
                    value={value ?? undefined}
                />
            )}
            {!multiline && (
                <input
                    type={inputType}
                    inputMode={inputMode}
                    id={`${elementId}_INPUT`}
                    {...ariaAttributes}
                    name={variable.elementId}
                    placeholder={placeholder}
                    ref={elementReference}
                    min={min}
                    max={max}
                    maxLength={maxLength}
                    onBlur={inputOnBlur}
                    onFocus={inputOnFocus}
                    onKeyDown={combinedOnKeyDown}
                    onKeyPress={onKeyPress}
                    onChange={onChange}
                    disabled={disabled}
                    value={value ?? undefined}
                    autoComplete="off"
                    style={{
                        textAlign: inputMode ? "right" : "inherit",
                    }}
                />
            )}
        </InputBox>
    );
}
